import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { WrenchIcon, PencilIcon, TrashIcon, PlusIcon, MicIcon, SquareIcon, XIcon, LoaderIcon, AlertCircleIcon, RefreshCwIcon, CheckCircle2Icon, SparklesIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { EditItemModal } from '../ui/EditItemModal';
import { EQUIPMENT_GENERATION_PROMPT, EQUIPMENT_VOICE_CORRECTION_PROMPT } from '../../config/aiPrompts';
import { useI18n } from '../../config/i18n';
import { getSavedApiKey, chatCompletionWithRetry, transcribeWithRetry } from '../../config/openai';
// ── Types ────────────────────────────────────────────────────────────────────
interface EquipmentItem {
  id: string;
  text: string;
  text_es?: string;
}
let eqIdCounter = 0;
function createEqItem(text: string, text_es?: string): EquipmentItem {
  return {
    id: `eq-${Date.now()}-${++eqIdCounter}`,
    text,
    text_es
  };
}
// ── Main Component ───────────────────────────────────────────────────────────
export function EquipmentScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const {
    lang,
    t
  } = useI18n();
  // ── State ──────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<EquipmentItem[]>(() => {
    if (!data.taskEquipment || data.taskEquipment.length === 0) return [];
    return data.taskEquipment.map((eq, i) => createEqItem(eq, data.taskEquipment_es?.[i]));
  });
  // AI Generation State
  const [isAnalyzing, setIsAnalyzing] = useState(() => {
    return (!data.taskEquipment || data.taskEquipment.length === 0) && !!data.taskDescription;
  });
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const hasRunAI = useRef(false);
  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{
    id: string;
    text: string;
  } | null>(null);
  // Voice Modal State
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [extractionApplied, setExtractionApplied] = useState(false);
  const [extractionSummary, setExtractionSummary] = useState('');
  // MediaRecorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // ── Sync to Parent ─────────────────────────────────────────────────────────
  const syncToParent = useCallback((newItems: EquipmentItem[]) => {
    updateData({
      taskEquipment: newItems.map(i => i.text),
      taskEquipment_es: newItems.map(i => i.text_es || i.text)
    });
  }, [updateData]);
  // ── Initial AI Generation ──────────────────────────────────────────────────
  useEffect(() => {
    if (hasRunAI.current) return;
    hasRunAI.current = true;
    if (items.length > 0) {
      setIsAnalyzing(false);
      return;
    }
    const apiKey = getSavedApiKey();
    if (!apiKey || !data.taskDescription) {
      setIsAnalyzing(false);
      // Fallback equipment if no API key
      const fallbackItems = [createEqItem('Power drill', 'Taladro eléctrico'), createEqItem('Hand tools', 'Herramientas manuales'), createEqItem('Ladder', 'Escalera')];
      setItems(fallbackItems);
      syncToParent(fallbackItems);
      return;
    }
    async function generateEquipment() {
      setIsAnalyzing(true);
      setAnalyzeError(null);
      try {
        const promptContent = `Task Description: ${data.taskDescription}\n\nMOP Steps:\n${data.jobSteps.map((s, i) => `${i + 1}. ${s}`).join('\n')}`;
        const completion = await chatCompletionWithRetry({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: EQUIPMENT_GENERATION_PROMPT
          }, {
            role: 'user',
            content: promptContent
          }],
          temperature: 0.3,
          max_tokens: 500
        });
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) {
          jsonStr = fenceMatch[1].trim();
        }
        const parsed = JSON.parse(jsonStr) as {
          equipment: string[];
          equipment_es?: string[];
        };
        if (!Array.isArray(parsed.equipment) || parsed.equipment.length === 0) {
          throw new Error('Invalid response structure');
        }
        const newItems = parsed.equipment.map((eq, i) => createEqItem(eq, parsed.equipment_es?.[i]));
        setItems(newItems);
        syncToParent(newItems);
      } catch (err: any) {
        console.error('[AI Equipment Generation] Failed:', err);
        if (err?.status === 401) {
          setAnalyzeError('Invalid OpenAI API key. You can add equipment manually.');
        } else {
          setAnalyzeError(`AI generation failed: ${err?.message || String(err)}`);
        }
      } finally {
        setIsAnalyzing(false);
      }
    }
    generateEquipment();
  }, [data.taskDescription, data.jobSteps, items.length, syncToParent]);
  // Cleanup streams
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);
  // ── Item Handlers ──────────────────────────────────────────────────────────
  const handleRemove = (id: string) => {
    const newItems = items.filter(i => i.id !== id);
    setItems(newItems);
    syncToParent(newItems);
  };
  const handleEditStart = (item: EquipmentItem) => {
    setEditingItem({
      id: item.id,
      text: lang === 'es' && item.text_es ? item.text_es : item.text
    });
    setIsEditModalOpen(true);
  };
  const handleEditSave = (newText: string) => {
    if (!editingItem) return;
    const newItems = items.map(item => {
      if (item.id === editingItem.id) {
        // If editing in Spanish, only update Spanish text. Otherwise English.
        if (lang === 'es') {
          return {
            ...item,
            text_es: newText
          };
        }
        return {
          ...item,
          text: newText
        };
      }
      return item;
    });
    setItems(newItems);
    syncToParent(newItems);
    setIsEditModalOpen(false);
    setEditingItem(null);
  };
  const handleAdd = () => {
    const newItem = createEqItem('New equipment');
    const newItems = [...items, newItem];
    setItems(newItems);
    syncToParent(newItems);
    handleEditStart(newItem);
  };
  const handleRetryAnalysis = () => {
    hasRunAI.current = false;
    setItems([]);
  };
  // ── Voice Recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      setVoiceTranscription('');
      setHasRecorded(false);
      setExtractionApplied(false);
      setExtractionSummary('');
    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceError('Microphone access denied. Please allow permission and try again.');
    }
  }, []);
  const stopRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      setVoiceError('Recording failed. Please try again.');
      return;
    }
    setIsRecording(false);
    setIsTranscribing(true);
    setVoiceError(null);
    const audioBlob = await new Promise<Blob>(resolve => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunksRef.current, {
          type: 'audio/webm'
        }));
      };
      mediaRecorder.stop();
    });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioBlob.size < 1000) {
      setIsTranscribing(false);
      setVoiceError('Recording too short. Speak for at least 2 seconds.');
      return;
    }
    try {
      const file = new File([audioBlob], 'equipment.webm', {
        type: 'audio/webm'
      });
      const response = await transcribeWithRetry(file, {
        model: 'whisper-1',
        language: lang === 'es' ? 'es' : 'en'
      });
      const rawText = response.text.trim();
      if (!rawText) {
        setIsTranscribing(false);
        setVoiceError('No speech detected. Please speak clearly and try again.');
        return;
      }
      setVoiceTranscription(rawText);
      setHasRecorded(true);
      setIsTranscribing(false);
    } catch (err: any) {
      console.error('[Whisper] Transcription failed:', err);
      setIsTranscribing(false);
      const statusCode = err?.status || err?.statusCode;
      if (statusCode === 401) {
        setVoiceError('Invalid OpenAI API key.');
      } else if (statusCode === 429) {
        setVoiceError('Rate limit reached. Please wait and try again.');
      } else {
        setVoiceError(`Transcription failed: ${err?.message || String(err)}`);
      }
    }
  }, [lang]);
  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  // ── Apply Voice Extraction ─────────────────────────────────────────────────
  const handleApplyExtraction = async () => {
    if (!voiceTranscription) return;
    setIsExtracting(true);
    setVoiceError(null);
    try {
      const currentList = items.map(i => i.text).join('\n- ');
      const userMessage = `Current equipment list:\n- ${currentList}\n\nVoice corrections:\n${voiceTranscription}`;
      const completion = await chatCompletionWithRetry({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: EQUIPMENT_VOICE_CORRECTION_PROMPT
        }, {
          role: 'user',
          content: userMessage
        }],
        temperature: 0.3,
        max_tokens: 500
      });
      const raw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = raw;
      const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr) as {
        equipment: string[];
        equipment_es?: string[];
      };
      if (!Array.isArray(parsed.equipment) || parsed.equipment.length === 0) {
        throw new Error('No equipment detected in response');
      }
      const updatedItems = parsed.equipment.map((eq, i) => createEqItem(eq, parsed.equipment_es?.[i]));
      setItems(updatedItems);
      syncToParent(updatedItems);
      const diff = updatedItems.length - items.length;
      const summary = diff > 0 ? `Updated list — ${diff} item${diff !== 1 ? 's' : ''} added` : diff < 0 ? `Updated list — ${Math.abs(diff)} item${Math.abs(diff) !== 1 ? 's' : ''} removed` : `Updated list — ${updatedItems.length} items`;
      setExtractionApplied(true);
      setExtractionSummary(summary);
      setTimeout(() => {
        setShowVoiceModal(false);
        setTimeout(() => {
          setHasRecorded(false);
          setVoiceTranscription('');
          setExtractionApplied(false);
          setExtractionSummary('');
        }, 300);
      }, 1200);
    } catch (err: any) {
      console.error('[GPT Extraction] Failed:', err);
      setVoiceError(`Failed to extract equipment: ${err?.message || String(err)}`);
    } finally {
      setIsExtracting(false);
    }
  };
  // ── Render Loading ─────────────────────────────────────────────────────────
  if (isAnalyzing) {
    return <div className="flex flex-col h-full items-center justify-center" data-id="element-337">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'linear'
      }} className="mb-5" data-id="element-338">
          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-339" />
        </motion.div>
        <p className="text-gray-900 font-semibold text-base mb-1" data-id="element-340">
          Identifying equipment…
        </p>
        <p className="text-gray-400 text-xs text-center max-w-[220px]" data-id="element-341">
          Analyzing task description and steps to suggest required tools.
        </p>
      </div>;
  }
  // ── Main Render ────────────────────────────────────────────────────────────
  return <div className="flex flex-col h-full relative" data-id="element-342">
      <div className="mb-3" data-id="element-343">
        <div className="flex items-center justify-between mb-2" data-id="element-344">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-345">
            {t('equip.title')}
          </h2>
          <button onClick={() => {
          setShowVoiceModal(true);
          setVoiceError(null);
          setExtractionApplied(false);
          setExtractionSummary('');
        }} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" data-id="element-346">
            <MicIcon className="w-3.5 h-3.5 text-yellow-500" data-id="element-347" />
            {t('equip.voiceCorrect')}
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-4" data-id="element-348">{t('equip.voiceDesc')}</p>

        <div className="bg-white border border-gray-200 rounded-xl p-3 border-l-4 border-l-yellow-500" data-id="element-349">
          <div className="flex items-center gap-2" data-id="element-350">
            <SparklesIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" data-id="element-351" />
            <h3 className="text-gray-900 font-bold text-base leading-snug" data-id="element-352">
              {lang === 'es' && data.taskTitle_es ? data.taskTitle_es : data.taskTitle || 'Task Equipment'}
            </h3>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-353">
        {analyzeError && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-3" data-id="element-354">
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 flex items-start gap-2.5" data-id="element-355">
              <AlertCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-356" />
              <div className="flex-1 min-w-0" data-id="element-357">
                <p className="text-amber-700 text-xs leading-relaxed" data-id="element-358">
                  {analyzeError}
                </p>
              </div>
              <button onClick={handleRetryAnalysis} className="flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-600 text-[10px] font-bold uppercase tracking-wider flex-shrink-0 hover:bg-amber-200 transition-colors" data-id="element-359">
                <RefreshCwIcon className="w-3 h-3" data-id="element-360" />
                Retry
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto pb-20 -mx-4 px-4" data-id="element-361">
        {items.length === 0 ? <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center mt-4" data-id="element-362">
            <p className="text-gray-400 text-sm font-medium mb-1" data-id="element-363">
              {t('equip.noEquipment')}
            </p>
            <p className="text-gray-400 text-xs" data-id="element-364">
              Add tools manually or use voice to describe them
            </p>
          </div> : <div className="space-y-3 mt-2" data-id="element-365">
            <AnimatePresence data-id="element-366">
              {items.map((item, index) => <motion.div key={item.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            delay: index * 0.05
          }} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3" data-id="element-367">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-368">
                    <WrenchIcon className="w-4 h-4 text-gray-400" data-id="element-369" />
                  </div>
                  <p className="flex-1 text-gray-800 text-sm leading-snug" data-id="element-370">
                    {lang === 'es' && item.text_es ? item.text_es : item.text}
                  </p>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-371">
                    <button onClick={() => handleEditStart(item)} className="p-2 text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-372">
                      <PencilIcon className="w-4 h-4" data-id="element-373" />
                    </button>
                    <button onClick={() => handleRemove(item.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" data-id="element-374">
                      <TrashIcon className="w-4 h-4" data-id="element-375" />
                    </button>
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </div>}

        <motion.button whileTap={{
        scale: 0.98
      }} onClick={handleAdd} className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 font-medium text-sm flex items-center justify-center gap-2 hover:border-gray-400 hover:text-gray-700 transition-colors" data-id="element-376">
          <PlusIcon className="w-4 h-4" data-id="element-377" />
          {t('equip.addItem')}
        </motion.button>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-378">
        <PrimaryButton onClick={onNext} disabled={items.length === 0} data-id="element-379">
          {t('continue')}
        </PrimaryButton>
      </div>

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      <EditItemModal isOpen={isEditModalOpen} title={t('equip.editEquipment')} initialText={editingItem?.text || ''} onSave={handleEditSave} onCancel={() => {
      setIsEditModalOpen(false);
      setEditingItem(null);
    }} data-id="element-380" />

      {/* ── Voice Correction Modal ──────────────────────────────────────────── */}
      <AnimatePresence data-id="element-381">
        {showVoiceModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !isRecording && !isTranscribing && !isExtracting && setShowVoiceModal(false)} className="absolute inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-382" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 rounded-t-2xl z-50 px-5 pt-4 pb-6 max-h-[75%] flex flex-col overflow-hidden" data-id="element-383">
              <div className="flex items-center justify-between mb-2" data-id="element-384">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-385">
                  {t('equip.voiceCorrect')}
                </h3>
                <button onClick={() => setShowVoiceModal(false)} disabled={isRecording || isTranscribing || isExtracting} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100 disabled:opacity-50" data-id="element-386">
                  <XIcon className="w-5 h-5" data-id="element-387" />
                </button>
              </div>

              <p className="text-gray-500 text-sm mb-4" data-id="element-388">
                {t('equip.voiceDesc')}
              </p>

              {extractionApplied ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex-1 flex flex-col items-center justify-center space-y-3" data-id="element-389">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 15
            }} data-id="element-390">
                    <CheckCircle2Icon className="w-12 h-12 text-green-600" data-id="element-391" />
                  </motion.div>
                  <p className="text-gray-900 font-semibold text-sm" data-id="element-392">
                    {t('equip.extracted')}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-393">{extractionSummary}</p>
                </motion.div> : <>
                  <div className="flex-1 flex flex-col items-center justify-center space-y-4 mb-4 min-h-0" data-id="element-394">
                    {isExtracting ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex flex-col items-center space-y-3" data-id="element-395">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1.2,
                  ease: 'linear'
                }} data-id="element-396">
                          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-397" />
                        </motion.div>
                        <p className="text-gray-900 font-medium text-sm" data-id="element-398">
                          {t('equip.extracting')}
                        </p>
                      </motion.div> : isTranscribing ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex flex-col items-center space-y-3" data-id="element-399">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} data-id="element-400">
                          <LoaderIcon className="w-10 h-10 text-blue-600" data-id="element-401" />
                        </motion.div>
                        <p className="text-gray-900 font-medium text-sm" data-id="element-402">
                          Transcribing…
                        </p>
                      </motion.div> : <>
                        <div className="relative flex items-center justify-center" data-id="element-403">
                          {isRecording && <motion.div animate={{
                    scale: [1, 1.3, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5
                  }} className="absolute w-24 h-24 bg-yellow-100 rounded-full" data-id="element-404" />}
                          {isRecording && <motion.div animate={{
                    scale: [1, 1.15, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    delay: 0.2
                  }} className="absolute w-20 h-20 bg-yellow-200 rounded-full" data-id="element-405" />}
                          <motion.button whileTap={{
                    scale: 0.9
                  }} onClick={handleRecordToggle} className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-colors ${isRecording ? 'bg-red-500 shadow-red-400/30' : 'bg-yellow-400 shadow-yellow-400/30'}`} data-id="element-406">
                            {isRecording ? <SquareIcon className="w-6 h-6 text-white fill-current" data-id="element-407" /> : <MicIcon className="w-6 h-6 text-gray-900" data-id="element-408" />}
                          </motion.button>
                        </div>

                        <div className="text-center h-6" data-id="element-409">
                          <p className={`font-medium text-sm ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} data-id="element-410">
                            {isRecording ? 'Recording…' : hasRecorded ? 'Tap to re-record' : 'Tap to start'}
                          </p>
                        </div>
                      </>}

                    {voiceError && <p className="text-red-500 text-xs text-center max-w-[250px]" data-id="element-411">
                        {voiceError}
                      </p>}

                    {hasRecorded && voiceTranscription && !isExtracting && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} transition={{
                duration: 0.3
              }} className="w-full p-3 rounded-xl bg-gray-50 border border-gray-200 overflow-y-auto max-h-[20vh]" data-id="element-412">
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-413">
                          Transcription
                        </p>
                        <p className="text-gray-700 text-sm leading-snug" data-id="element-414">
                          "{voiceTranscription}"
                        </p>
                      </motion.div>}
                  </div>

                  {!isTranscribing && !isExtracting && <PrimaryButton onClick={handleApplyExtraction} disabled={!hasRecorded || !voiceTranscription} data-id="element-415">
                      Apply to Equipment List
                    </PrimaryButton>}
                </>}
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}